/* ===================================================================
   useNotifications() -- the client's notification feed for the bell +
   Dashboard "Needs attention" panel.

   Binds to GET /api/v1/notifications (engine/api/v1/app.py) through the
   typed api client (src/lib/api.ts). Disabled until a Bearer rr_ token
   exists, so it never auto-fires a 401 on first paint.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, type AdminTenantDashboard, type NotificationsResponse } from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { adminDashboardKey } from './useAdminTenantDashboard';
import { qk } from './queryKeys';

export interface UseNotificationsParams {
  /** Filter to unread rows only. */
  unread?: boolean;
  /** Cap the feed (server default 50, max 200). */
  limit?: number;
  /** Force-disable even if a token exists. */
  enabled?: boolean;
}

export function useNotifications(
  params: UseNotificationsParams = {},
): UseQueryResult<NotificationsResponse, Error> {
  const { enabled, ...query } = params;
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, select the notifications slice out
  // of the single admin dashboard bundle (one cached fetch shared across panels).
  return useQuery({
    queryKey: admin ? adminDashboardKey(admin.clientId) : qk.notifications(query),
    queryFn: ({ signal }) =>
      admin
        ? (api.getTenantDashboard(admin.clientId, signal) as Promise<unknown>)
        : (api.getNotifications(query, signal) as Promise<unknown>),
    select: admin
      ? (d) => (d as AdminTenantDashboard).notifications
      : undefined,
    enabled: (admin ? true : hasToken) && enabled !== false,
    staleTime: 30_000,
  }) as UseQueryResult<NotificationsResponse, Error>;
}

/* ===================================================================
   useMarkNotifications() -- mark one or all alerts read (Brad, 2026-06-17).

   one()    -> POST /api/v1/notifications/{id}/read
   all()    -> POST /api/v1/notifications/read-all

   After either, we invalidate every notifications query so the bell badge
   + the "Needs attention" panel refetch the new unread_count.
   =================================================================== */
export interface UseMarkNotificationsResult {
  markRead: (id: string) => Promise<unknown>;
  markAllRead: () => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
}

export function useMarkNotifications(): UseMarkNotificationsResult {
  const qc = useQueryClient();
  const admin = useAdminView();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.notifications() });
  };
  // Hooks are always called (Rules of Hooks); the returned object is swapped
  // for inert no-ops when in an operator read-only view.
  const oneM = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: invalidate,
  });
  const allM = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: invalidate,
  });
  // OPTION A read-only: an operator viewing a client's dashboard cannot mark
  // that client's notifications read. Inert no-ops (defense-in-depth -- the
  // backend has no admin mark-read endpoint either).
  if (admin) {
    return {
      markRead: async () => undefined,
      markAllRead: async () => undefined,
      isPending: false,
      error: null,
    };
  }
  return {
    markRead: (id) => oneM.mutateAsync(id),
    markAllRead: () => allM.mutateAsync(),
    isPending: oneM.isPending || allM.isPending,
    error: oneM.error ?? allM.error ?? null,
  };
}
